import dayjs from "dayjs"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { getUserFullName } from "../../../helpers/userDisplay"
import { PHOTOGRAPHY_DETAIL_WITH } from "../../CityHub/cityPhotography"

/** Relations for Admin Photography list cards. */
export const ADMIN_PHOTOGRAPHY_LIST_WITH = [
  "tags",
  "medias",
  "uploader",
  "city",
]

const personName = user => {
  if (!user) return "--"
  return getUserFullName(user, { fallback: "--" })
}

const formatPhotoDate = value => {
  if (!value) return "--"
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "--"
}

const firstMedia = medias => {
  if (!Array.isArray(medias) || medias.length === 0) return null
  return medias[0]
}

const unwrapEntity = result => {
  if (!result) return null
  if (
    result?.data &&
    typeof result.data === "object" &&
    !Array.isArray(result.data)
  ) {
    return result.data
  }
  return result
}

/**
 * Map GET /api/v1/admin/photography item → card row.
 */
export const mapAdminPhotographyRow = item => {
  const media = firstMedia(item?.medias)
  const mime = String(media?.mime_type || "").toLowerCase()
  const isVideo = mime.startsWith("video/")
  const status = String(item?.status || "")
    .trim()
    .toLowerCase()
  const uploader = item?.uploader || null
  const likesRaw = item?.likes_count ?? item?.likes ?? item?.reactions_count
  const likes = Number(likesRaw)

  return {
    id: item?.id,
    title: item?.title || "--",
    description: item?.description || "",
    date: formatPhotoDate(item?.created_at),
    status: status || "awaiting",
    image:
      media?.thumbnail_path_url ||
      media?.path_url ||
      media?.media_url ||
      "",
    isVideo,
    mimeType: mime,
    photographer: {
      name: personName(uploader),
      avatar: getUserAvatarUrl(uploader),
    },
    likes: Number.isFinite(likes) ? Math.max(0, likes) : 0,
    liked: Boolean(item?.has_current_user_liked ?? item?.has_reacted),
    tags: Array.isArray(item?.tags)
      ? item.tags.map(tag => tag?.name).filter(Boolean)
      : [],
    cityId: item?.city?.id || "",
    cityLabel: item?.city
      ? [item.city.city, item.city.state || item.city.country]
          .filter(Boolean)
          .join(", ")
      : "--",
    raw: item,
  }
}

/**
 * Detail fetch for admin/city photography preview (full album).
 * GET /api/v1/admin/photography/:id
 */
export const fetchAdminPhotographyDetail = async (id, { signal } = {}) => {
  if (!id) return { ok: false }

  const response = await get(endpoints.getAdminPhotographyById(id), {
    params: { "with[]": PHOTOGRAPHY_DETAIL_WITH },
    signal,
  })

  if (signal?.aborted) return { ok: false, aborted: true }
  if (!response) return { ok: false, aborted: Boolean(signal?.aborted) }

  if (!response?.data?.success) {
    toastApiError(response, "Failed to load photography")
    return { ok: false }
  }

  const item = unwrapEntity(response.data.result)
  if (!item?.id) {
    toastApiError(response, "Failed to load photography")
    return { ok: false }
  }

  return { ok: true, item }
}
